import React, {useState} from "react";
import { Button, Checkbox, Control, Delete, Field, Icon, Input } from "@allxsmith/bestax-bulma";
import {getPartCustomerCost, getPartCustomerEstimatedCost} from "../../utils";
import {AsyncTypeahead} from "react-bootstrap-typeahead";
import axios from "axios";
import useEditableTableTextAreaCell from "../../hooks/useEditableTableTextAreaCell";
import {faPoundSign} from "@fortawesome/free-solid-svg-icons";
import useEditableTableDateCell from "../../hooks/useEditableTableDateCell";
import TrackingOptionsPicker from "../Settings/TrackingCategory/TrackingOptionsPicker";
import TaxTypePicker from "../Settings/NominalCodes/TaxTypePicker";


function Part({
                part,
                index,
                customer,
                updateParts,
                removePart,
                onSearchPartClick,
                quote,
                invoiced,
                appDefaults,
                trackingOptions,
                trackingOptionObjects
              }) {
  const [isLoading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [, setTypeahead] = useState();

  const handleChange = e => {
    const newPart = getNewPart(e);
    updateParts(newPart, index);
  };

  const onTrackingOptionChange = e => {
      const toUpdate = {
          target: {
              name: e.target.name,
              value: trackingOptionObjects[e.target.value]
          }
      };
      handleChange(toUpdate);
  };

  const onSearch = e => {
    onSearchPartClick(part, index);
  };

  const getNewPart = e => {
    let newPart = {...part, [e.target.name]: e.target.value};

    if (e.target.name === "cost") {
      newPart.customer_cost = getPartCustomerCost(newPart, customer, appDefaults);
    }
    if (
      e.target.name === "estimated_cost"
    ) {
      newPart.estimated_cost = e.target.value;
      newPart.estimated_customer_cost = getPartCustomerEstimatedCost(newPart, customer, appDefaults)
    }
    return newPart;
  };

  const handleSearch = e => {
    setLoading(true);
    axios.get(`/api/contacts?contact_type=SP&search=${e}`).then(resp => {
      setSearchData(resp.data.results);
      setLoading(false);
    });
  };

  const handlePartSupplierSelect = supplier => {
    let newPart = {...part, supplier: supplier[0]};
    updateParts(newPart, index);
  };

  const onCheck = prop => {
    let newPart = {...part, [prop]: !part[prop]};
    if (prop === "cost_override" && newPart[prop] === false) {
      newPart.customer_cost = getPartCustomerCost(newPart, customer, appDefaults);
      newPart.estimated_customer_cost = getPartCustomerEstimatedCost(newPart, customer, appDefaults)
    }
    if (prop === "from_stock" && newPart[prop] === true){
        // if from stock has been ticked to true
        newPart.cost_override = true
    }
    updateParts(newPart, index);
  };

  const getDisabled = (field) => {
      // If the part has been approved and the box has been filled then it should be disabled
      if(part.approved){
          if(part[field] && part[field].id){
              return true
          }
      }
      return false
  }

  // TODO(bestax-migrate): bestax Icon renders from a `name` prop, not children; convert this icon markup by hand
  // TODO(bestax-migrate): `align` — inside a Control use its hasIconsLeft/iconLeft (or right) props instead
  return (
    <tr>
      <td>
        <Field className="has-addons">
          <Control>
            <Input
              type="text"
              value={part.name}
              name="name"
              onChange={handleChange}
              disabled={invoiced}
              data-testid="part-name"
            />
          </Control>
          <Control>
            <Button
              data-testid="search-button"
              color="success"
              onClick={onSearch}
            >
              S
            </Button>
          </Control>
        </Field>
      </td>
      {quote && (
        <td>
          <Control>
            <Input
              data-testid="est-cost-input"
              className="input"
              type="number"
              value={String(part.estimated_cost)}
              disabled={invoiced}
              name="estimated_cost"
              onChange={handleChange}
            />
          </Control>
        </td>
      )}
      {quote && (
        <td>
          <Control>
            <Input
              type="number"
              name="estimated_customer_cost"
              onChange={handleChange}
              value={String(part.estimated_customer_cost)}
              disabled={!part.cost_override || invoiced}
            />
          </Control>
        </td>
      )}
      <td>
        <Control>
          <AsyncTypeahead
            useCache={false}
            id="typeahead"
            labelKey="display_name"
            minLength={2}
            name={"supplier"}
            onSearch={handleSearch}
            onChange={handlePartSupplierSelect}
            placeholder={"Search Supplier"}
            options={searchData}
            ref={typeahead => setTypeahead(typeahead)}
            className="typeahead"
            isLoading={isLoading}
            selected={part.supplier && part.supplier.id ? [part.supplier] : []}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <TrackingOptionsPicker
            name={"supplier_tracking_option"}
            options={trackingOptions}
            blank={true}
            disabled={getDisabled("supplier_tracking_option")}
            inlineEdit={false}
            editable={true}
            value={part.supplier_tracking_option}
            onChange={onTrackingOptionChange}
            className={" "}
        />
      </td>
      <td>
          <TrackingOptionsPicker
            name={"customer_tracking_option"}
            options={trackingOptions}
            blank={true}
            disabled={false}
            inlineEdit={false}
            editable={true}
            value={part.customer_tracking_option}
            onChange={onTrackingOptionChange}
            className={" "}
          />
      </td>
      <td>
        <Control>
          <Input
            type="date"
            value={part.date_ordered || ""}
            name="date_ordered"
            onChange={handleChange}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Input
            type="text"
            value={part.part_number}
            name="part_number"
            onChange={handleChange}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Input
            type="text"
            value={part.part_invoice_number}
            name="part_invoice_number"
            onChange={handleChange}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Input
            data-testid="cost-input"
            className="input"
            type="number"
            value={String(part.cost)}
            name="cost"
            onChange={handleChange}
            disabled={!!invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Input
            type="number"
            name="customer_cost"
            onChange={handleChange}
            value={part.customer_cost}
            checked={part.customer_cost}
            disabled={!part.cost_override || invoiced}
          />
        </Control>
      </td>
      <td>
          <TaxTypePicker
            name={"cost_vat"}
            value={part.cost_vat}
            onChange={handleChange}
            disabled={!!invoiced}
          />
      </td>
      {useEditableTableTextAreaCell(
        {
          propName: "comments",
          value: part.comments,
          onChange: handleChange,
          editable: true,
        }
      )}
      <td>
        <Control>
          <Checkbox
            type="checkbox"
            name="cost_override"
            onChange={() => onCheck("cost_override")}
            checked={part.cost_override}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Checkbox
            type="checkbox"
            name="is_postage"
            onChange={() => onCheck("is_postage")}
            checked={part.is_postage}
            disabled={invoiced}
          />
        </Control>
      </td>
      <td>
        <Control>
          <Checkbox
            type="checkbox"
            name="from_stock"
            onChange={() => onCheck("from_stock")}
            checked={part.from_stock}
            disabled={invoiced}
          />
        </Control>
      </td>
      {useEditableTableDateCell({
          propName: "backorder_expected_date",
          value: part.backorder_expected_date,
          onChange: handleChange,
          editable: true,
          inlineEdit: false
      })}
      <td>
        <Control>
          <Checkbox
            type="checkbox"
            name="to_be_ordered"
            onChange={() => onCheck("to_be_ordered")}
            checked={part.to_be_ordered}
            disabled={invoiced}
          />
        </Control>
      </td>
      <Control>
        <Checkbox
          type="checkbox"
          name="arrived"
          onChange={() => onCheck("arrived")}
          checked={part.arrived}
          disabled={invoiced}
        />
      </Control>
      <td>
        <Delete onClick={() => removePart(index)} data-testid="remove-part">
          Remove
        </Delete>
      </td>
    </tr>
  );
}

export default Part;
